import React from 'react';

/* ── CSS keyframes — injected once ─────────────────────────────── */
const CSS = `
@keyframes hf1{0%,100%{transform:translate(0,0)rotate(-3deg)}33%{transform:translate(8px,-16px)rotate(3.5deg)}66%{transform:translate(-5px,-10px)rotate(-1.5deg)}}
@keyframes hf2{0%,100%{transform:translate(0,0)rotate(3deg)}35%{transform:translate(-8px,-15px)rotate(-3.5deg)}70%{transform:translate(5px,-9px)rotate(2deg)}}
@keyframes hf3{0%,100%{transform:translate(0,0)rotate(-2deg)}40%{transform:translate(5px,-19px)rotate(3deg)}75%{transform:translate(-7px,-8px)rotate(-4deg)}}
@keyframes hf4{0%,100%{transform:translate(0,0)rotate(4deg)}25%{transform:translate(-6px,-14px)rotate(-4deg)}60%{transform:translate(9px,-9px)rotate(1.5deg)}}
@keyframes hf5{0%,100%{transform:translate(0,0)rotate(-3.5deg)}45%{transform:translate(7px,-18px)rotate(3deg)}80%{transform:translate(-4px,-8px)rotate(-2deg)}}
@keyframes hf6{0%,100%{transform:translate(0,0)rotate(2.5deg)}30%{transform:translate(-9px,-13px)rotate(-3deg)}70%{transform:translate(6px,-15px)rotate(4deg)}}
.ts-hrt{animation-timing-function:ease-in-out;animation-iteration-count:infinite}
`;
if (typeof document !== 'undefined' && !document.getElementById('ts-hrt-css')) {
  const s = document.createElement('style');
  s.id = 'ts-hrt-css';
  s.textContent = CSS;
  document.head.appendChild(s);
}

/* ── Unique gradient IDs ─────────────────────────────────────── */
let _hid = 0;
const nextId = () => `fh${++_hid}`;

/* ── Heart path — viewBox "0 0 32 30" ── */
const HP = 'M16 27.5C16 27.5 2 18.5 2 9.5C2 5.36 5.36 2 9.5 2C11.87 2 14 3.18 16 5C18 3.18 20.13 2 22.5 2C26.64 2 30 5.36 30 9.5C30 18.5 16 27.5 16 27.5Z';
const CX = 16, CY = 14.75;

/* ── Premium glossy pink heart (🩷) ─────────────────────────── */
const PinkHeart = ({ size }) => {
  const id = React.useRef(nextId()).current;
  const sh = Math.round(size * 0.10);
  const sb = Math.round(size * 0.24);

  const layers = [
    { scale: 1.00, c0: '#FFF0F6', c1: '#FFD0E8' },
    { scale: 0.78, c0: '#FFB4D8', c1: '#FF80B8' },
    { scale: 0.56, c0: '#FF58AC', c1: '#E82496' },
    { scale: 0.34, c0: '#FF50A8', c1: '#E040A0' },
    { scale: 0.14, c0: '#FF60B0', c1: '#F048A8' },
  ];

  return (
    <svg width={size} height={Math.round(size * 0.94)} viewBox="0 0 32 30" fill="none"
      style={{ display:'block', filter:`drop-shadow(0 ${sh}px ${sb}px rgba(230,48,138,0.65))` }}>
      <defs>
        {layers.map((l, i) => (
          <radialGradient key={i} id={`${id}-${i}`}
            cx="10" cy="8" r="20" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor={l.c0} />
            <stop offset="100%" stopColor={l.c1} />
          </radialGradient>
        ))}
        {/* Primary glossy highlight — upper-left gleam */}
        <radialGradient id={`${id}-hl`}
          cx="10" cy="7" r="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="white" stopOpacity="0.94" />
          <stop offset="42%"  stopColor="white" stopOpacity="0.30" />
          <stop offset="100%" stopColor="white" stopOpacity="0"    />
        </radialGradient>
        {/* Secondary inner warmth glow */}
        <radialGradient id={`${id}-gw`}
          cx="22" cy="21" r="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#FFB8D8" stopOpacity="0.38" />
          <stop offset="100%" stopColor="#FFB8D8" stopOpacity="0"    />
        </radialGradient>
      </defs>

      {layers.map((l, i) => (
        <path key={i} d={HP} fill={`url(#${id}-${i})`}
          transform={`translate(${CX},${CY}) scale(${l.scale}) translate(${-CX},${-CY})`} />
      ))}
      {/* Warm bottom glow */}
      <path d={HP} fill={`url(#${id}-gw)`} />
      {/* Glossy top highlight */}
      <path d={HP} fill={`url(#${id}-hl)`} />
    </svg>
  );
};

/* ── Dreamy lavender heart (💜) ─────────────────────────────── */
const LavHeart = ({ size }) => {
  const id = React.useRef(nextId()).current;
  const sh = Math.round(size * 0.08);
  const sb = Math.round(size * 0.20);

  const layers = [
    { scale: 1.00, c0: '#F4ECFF', c1: '#E2CCFF' },
    { scale: 0.78, c0: '#CCA8FF', c1: '#AC7CF8' },
    { scale: 0.56, c0: '#9850EC', c1: '#7830D8' },
    { scale: 0.34, c0: '#9048E8', c1: '#7428D0' },
    { scale: 0.14, c0: '#9C58EC', c1: '#8040DC' },
  ];

  return (
    <svg width={size} height={Math.round(size * 0.94)} viewBox="0 0 32 30" fill="none"
      style={{ display:'block', filter:`drop-shadow(0 ${sh}px ${sb}px rgba(138,80,220,0.58))` }}>
      <defs>
        {layers.map((l, i) => (
          <radialGradient key={i} id={`${id}-${i}`}
            cx="10" cy="8" r="20" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor={l.c0} />
            <stop offset="100%" stopColor={l.c1} />
          </radialGradient>
        ))}
        <radialGradient id={`${id}-hl`}
          cx="10" cy="7" r="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="white" stopOpacity="0.86" />
          <stop offset="48%"  stopColor="white" stopOpacity="0.20" />
          <stop offset="100%" stopColor="white" stopOpacity="0"    />
        </radialGradient>
      </defs>

      {layers.map((l, i) => (
        <path key={i} d={HP} fill={`url(#${id}-${i})`}
          transform={`translate(${CX},${CY}) scale(${l.scale}) translate(${-CX},${-CY})`} />
      ))}
      <path d={HP} fill={`url(#${id}-hl)`} />
    </svg>
  );
};

/* ── Warm rose heart (💗) — New Mother brand style ──────────── */
const WarmHeart = ({ size }) => {
  const id = React.useRef(nextId()).current;
  const sh = Math.round(size * 0.09);
  const sb = Math.round(size * 0.26);

  const layers = [
    { scale: 1.00, c0: '#FFF0F4', c1: '#FFD0DE' },
    { scale: 0.80, c0: '#FFACC8', c1: '#FF78A8' },
    { scale: 0.60, c0: '#FF5090', c1: '#E82272' },
    { scale: 0.38, c0: '#FF4888', c1: '#E83878' },
    { scale: 0.16, c0: '#FF5890', c1: '#F04480' },
  ];

  return (
    <svg width={size} height={Math.round(size * 0.94)} viewBox="0 0 32 30" fill="none"
      style={{ display:'block', filter:`drop-shadow(0 ${sh}px ${sb}px rgba(228,44,108,0.62)) drop-shadow(0 2px ${Math.round(size*0.12)}px rgba(255,130,175,0.36))` }}>
      <defs>
        {layers.map((l, i) => (
          <radialGradient key={i} id={`${id}-${i}`}
            cx="10" cy="8" r="20" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor={l.c0} />
            <stop offset="100%" stopColor={l.c1} />
          </radialGradient>
        ))}
        {/* Upper-left glossy highlight */}
        <radialGradient id={`${id}-hl`}
          cx="10" cy="7" r="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="white" stopOpacity="0.92" />
          <stop offset="38%"  stopColor="white" stopOpacity="0.26" />
          <stop offset="100%" stopColor="white" stopOpacity="0"    />
        </radialGradient>
        {/* Warm inner bloom — lower right */}
        <radialGradient id={`${id}-gw`}
          cx="21" cy="22" r="11" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#FFBCD4" stopOpacity="0.44" />
          <stop offset="100%" stopColor="#FFBCD4" stopOpacity="0"    />
        </radialGradient>
        {/* Soft outer rim glow */}
        <radialGradient id={`${id}-rim`}
          cx="16" cy="15" r="15" gradientUnits="userSpaceOnUse">
          <stop offset="72%"  stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="#FFD0E0" stopOpacity="0.18" />
        </radialGradient>
      </defs>

      {layers.map((l, i) => (
        <path key={i} d={HP} fill={`url(#${id}-${i})`}
          transform={`translate(${CX},${CY}) scale(${l.scale}) translate(${-CX},${-CY})`} />
      ))}
      <path d={HP} fill={`url(#${id}-gw)`} />
      <path d={HP} fill={`url(#${id}-rim)`} />
      <path d={HP} fill={`url(#${id}-hl)`} />
    </svg>
  );
};

/* ── Animation pool ─────────────────────────────────────────── */
const ANIMS = ['hf1','hf2','hf3','hf4','hf5','hf6'];

/* ── Heart sets — three size tiers per density ──────────────────
   L = large (44–52 px) — 2–3 statement hearts
   M = medium (28–38 px) — atmospheric float
   S = small (18–24 px) — subtle accent
   Pink:lavender ratio ≈ 2:1
──────────────────────────────────────────────────────────────── */
const SETS = {

  /* ── Auth pages (login / signup) ─────────────────────────── */
  auth: [
    // Large — edges
    { t:'2%',  l:'1%',  s:47, o:0.55, tp:'pink', a:0, d:'0s',   dr:'16s' },
    { t:'5%',  l:'82%', s:43, o:0.52, tp:'pink', a:2, d:'2.8s', dr:'18s' },
    { t:'77%', l:'1%',  s:45, o:0.51, tp:'lav',  a:4, d:'1.6s', dr:'17s' },
    { t:'86%', l:'81%', s:41, o:0.5, tp:'pink', a:1, d:'4.0s', dr:'15s' },
    // Large — middle
    { t:'15%', l:'38%', s:40, o:0.4, tp:'lav',  a:3, d:'2.2s', dr:'19s' },
    { t:'55%', l:'45%', s:38, o:0.39, tp:'pink', a:5, d:'3.8s', dr:'18s' },
    // Medium — edges
    { t:'24%', l:'0%',  s:36, o:0.47, tp:'pink', a:1, d:'1.2s', dr:'14s' },
    { t:'32%', l:'85%', s:32, o:0.43, tp:'lav',  a:3, d:'4.2s', dr:'15s' },
    { t:'52%', l:'1%',  s:38, o:0.48, tp:'pink', a:5, d:'0.6s', dr:'13s' },
    { t:'56%', l:'86%', s:30, o:0.42, tp:'lav',  a:0, d:'3.4s', dr:'16s' },
    { t:'68%', l:'83%', s:34, o:0.44, tp:'pink', a:3, d:'2.0s', dr:'14s' },
    // Medium — middle
    { t:'30%', l:'42%', s:30, o:0.37, tp:'pink', a:2, d:'1.8s', dr:'15s' },
    { t:'48%', l:'55%', s:28, o:0.35, tp:'lav',  a:4, d:'4.5s', dr:'14s' },
    { t:'72%', l:'35%', s:32, o:0.36, tp:'pink', a:1, d:'0.5s', dr:'16s' },
    // Small — spread
    { t:'42%', l:'1%',  s:22, o:0.39, tp:'lav',  a:2, d:'2.4s', dr:'12s' },
    { t:'46%', l:'88%', s:20, o:0.37, tp:'pink', a:4, d:'5.0s', dr:'11s' },
    { t:'92%', l:'11%', s:18, o:0.35, tp:'lav',  a:5, d:'1.0s', dr:'12s' },
    { t:'18%', l:'60%', s:18, o:0.32, tp:'pink', a:0, d:'3.5s', dr:'13s' },
    { t:'65%', l:'28%', s:20, o:0.34, tp:'lav',  a:3, d:'2.0s', dr:'12s' },
    { t:'82%', l:'52%', s:16, o:0.3, tp:'pink', a:5, d:'4.2s', dr:'11s' },
  ],

  /* ── Onboarding ───────────────────────────────────────────── */
  onboarding: [
    // Large — edges
    { t:'2%',  l:'2%',  s:43, o:0.54, tp:'pink', a:0, d:'0s',   dr:'16s' },
    { t:'4%',  l:'82%', s:40, o:0.5, tp:'pink', a:2, d:'3.0s', dr:'18s' },
    { t:'80%', l:'3%',  s:41, o:0.48, tp:'lav',  a:4, d:'1.8s', dr:'17s' },
    // Large — middle
    { t:'12%', l:'40%', s:38, o:0.39, tp:'lav',  a:1, d:'2.5s', dr:'19s' },
    { t:'50%', l:'48%', s:40, o:0.38, tp:'pink', a:3, d:'4.0s', dr:'18s' },
    // Medium — edges
    { t:'20%', l:'0%',  s:34, o:0.46, tp:'pink', a:1, d:'1.4s', dr:'14s' },
    { t:'35%', l:'85%', s:30, o:0.42, tp:'lav',  a:3, d:'4.5s', dr:'15s' },
    { t:'58%', l:'1%',  s:36, o:0.47, tp:'pink', a:5, d:'0.8s', dr:'13s' },
    { t:'72%', l:'84%', s:32, o:0.43, tp:'pink', a:0, d:'2.6s', dr:'14s' },
    // Medium — middle
    { t:'28%', l:'44%', s:28, o:0.35, tp:'pink', a:4, d:'1.5s', dr:'15s' },
    { t:'65%', l:'38%', s:30, o:0.36, tp:'lav',  a:2, d:'3.8s', dr:'14s' },
    { t:'78%', l:'55%', s:26, o:0.34, tp:'pink', a:5, d:'0.4s', dr:'13s' },
    // Small — spread
    { t:'46%', l:'87%', s:22, o:0.38, tp:'lav',  a:2, d:'2.8s', dr:'12s' },
    { t:'62%', l:'3%',  s:20, o:0.37, tp:'pink', a:4, d:'1.0s', dr:'11s' },
    { t:'90%', l:'80%', s:18, o:0.35, tp:'pink', a:1, d:'3.8s', dr:'12s' },
    { t:'40%', l:'30%', s:18, o:0.31, tp:'lav',  a:0, d:'2.2s', dr:'13s' },
    { t:'88%', l:'42%', s:16, o:0.3, tp:'pink', a:3, d:'5.5s', dr:'11s' },
  ],

  /* ── New Mother / Baby Care — warm rose ambient ──────────── */
  newmother: [
    // Large — edges
    { t:'1%',  l:'-2%', s:45, o:0.48, tp:'warm', a:0, d:'0s',   dr:'20s' },
    { t:'2%',  l:'85%', s:41, o:0.46, tp:'warm', a:2, d:'3.6s', dr:'22s' },
    { t:'78%', l:'-3%', s:43, o:0.44, tp:'lav',  a:4, d:'2.2s', dr:'21s' },
    { t:'84%', l:'84%', s:40, o:0.42, tp:'warm', a:1, d:'5.0s', dr:'20s' },
    // Large — middle
    { t:'18%', l:'42%', s:40, o:0.37, tp:'warm', a:3, d:'2.8s', dr:'22s' },
    { t:'55%', l:'35%', s:38, o:0.35, tp:'lav',  a:5, d:'4.2s', dr:'20s' },
    // Medium — edges
    { t:'26%', l:'-2%', s:34, o:0.39, tp:'warm', a:3, d:'1.6s', dr:'18s' },
    { t:'46%', l:'87%', s:30, o:0.35, tp:'lav',  a:5, d:'3.2s', dr:'17s' },
    { t:'64%', l:'-1%', s:32, o:0.38, tp:'warm', a:1, d:'0.8s', dr:'19s' },
    // Medium — middle
    { t:'35%', l:'50%', s:28, o:0.32, tp:'warm', a:0, d:'2.0s', dr:'18s' },
    { t:'70%', l:'45%', s:30, o:0.34, tp:'lav',  a:2, d:'3.5s', dr:'17s' },
    { t:'12%', l:'55%', s:26, o:0.31, tp:'warm', a:4, d:'1.2s', dr:'19s' },
    // Small — spread
    { t:'50%', l:'89%', s:20, o:0.28, tp:'warm', a:0, d:'2.8s', dr:'15s' },
    { t:'92%', l:'8%',  s:18, o:0.25, tp:'lav',  a:3, d:'1.4s', dr:'14s' },
    { t:'44%', l:'22%', s:18, o:0.3, tp:'warm', a:5, d:'4.8s', dr:'13s' },
    { t:'80%', l:'60%', s:16, o:0.27, tp:'lav',  a:1, d:'2.5s', dr:'14s' },
    { t:'22%', l:'68%', s:16, o:0.25, tp:'warm', a:4, d:'3.0s', dr:'15s' },
  ],

  /* ── Dashboard / inner app pages (fixed, behind content) ─── */
  layout: [
    // Large — edges
    { t:'2%',  l:'0%',  s:45, o:0.52, tp:'pink', a:0, d:'0s',   dr:'16s' },
    { t:'3%',  l:'83%', s:41, o:0.48, tp:'pink', a:2, d:'3.5s', dr:'18s' },
    { t:'82%', l:'0%',  s:43, o:0.5, tp:'lav',  a:4, d:'2.0s', dr:'17s' },
    { t:'88%', l:'82%', s:40, o:0.46, tp:'pink', a:1, d:'4.5s', dr:'16s' },
    // Large — middle
    { t:'10%', l:'38%', s:38, o:0.37, tp:'lav',  a:3, d:'2.2s', dr:'19s' },
    { t:'45%', l:'44%', s:40, o:0.35, tp:'pink', a:5, d:'3.8s', dr:'18s' },
    { t:'70%', l:'40%', s:38, o:0.34, tp:'lav',  a:0, d:'1.5s', dr:'20s' },
    // Medium — edges
    { t:'22%', l:'0%',  s:36, o:0.46, tp:'pink', a:1, d:'1.5s', dr:'14s' },
    { t:'40%', l:'85%', s:32, o:0.42, tp:'lav',  a:3, d:'4.8s', dr:'15s' },
    { t:'58%', l:'0%',  s:38, o:0.47, tp:'pink', a:5, d:'0.9s', dr:'13s' },
    { t:'74%', l:'84%', s:34, o:0.43, tp:'pink', a:0, d:'3.0s', dr:'14s' },
    // Medium — middle
    { t:'28%', l:'42%', s:30, o:0.35, tp:'pink', a:2, d:'1.8s', dr:'15s' },
    { t:'52%', l:'55%', s:28, o:0.34, tp:'lav',  a:4, d:'4.2s', dr:'14s' },
    { t:'78%', l:'35%', s:32, o:0.36, tp:'pink', a:1, d:'0.5s', dr:'16s' },
    { t:'16%', l:'58%', s:26, o:0.32, tp:'lav',  a:3, d:'3.0s', dr:'15s' },
    { t:'62%', l:'28%', s:28, o:0.34, tp:'pink', a:5, d:'2.5s', dr:'13s' },
    // Small — spread
    { t:'44%', l:'87%', s:22, o:0.38, tp:'lav',  a:2, d:'3.2s', dr:'12s' },
    { t:'66%', l:'1%',  s:20, o:0.36, tp:'pink', a:4, d:'1.2s', dr:'11s' },
    { t:'90%', l:'80%', s:18, o:0.35, tp:'lav',  a:5, d:'2.4s', dr:'12s' },
    { t:'35%', l:'25%', s:18, o:0.31, tp:'pink', a:0, d:'3.8s', dr:'13s' },
    { t:'55%', l:'70%', s:20, o:0.32, tp:'lav',  a:3, d:'1.5s', dr:'12s' },
    { t:'82%', l:'55%', s:16, o:0.3, tp:'pink', a:1, d:'5.0s', dr:'11s' },
    { t:'18%', l:'72%', s:18, o:0.3, tp:'lav',  a:4, d:'2.8s', dr:'13s' },
    { t:'94%', l:'40%', s:16, o:0.28, tp:'pink', a:2, d:'4.0s', dr:'12s' },
  ],
};

/* ── Main export ─────────────────────────────────────────────── */
const FloatingHearts = ({ density = 'layout', fixed = false }) => {
  const hearts = SETS[density] || SETS.layout;
  const pos = fixed ? 'fixed' : 'absolute';

  return (
    <div className="select-none pointer-events-none"
      style={{ position: pos, inset: 0, overflow: 'hidden', zIndex: 0 }}>
      {hearts.map((h, i) => (
        <div key={i} className="ts-hrt" style={{
          position:          'absolute',
          top:               h.t,
          left:              h.l,
          opacity:           h.o,
          animationName:     ANIMS[h.a % 6],
          animationDuration: h.dr,
          animationDelay:    h.d,
        }}>
          {h.tp === 'warm'
            ? <WarmHeart size={h.s} />
            : h.tp === 'pink'
            ? <PinkHeart size={h.s} />
            : <LavHeart  size={h.s} />}
        </div>
      ))}
    </div>
  );
};

export default FloatingHearts;
